import { useCallback, useState } from 'react';

/**
 * GOLD STANDARD for server-paginated `CustomTable` list pages.
 *
 * Use this for every list whose rows are fetched one page at a time from the
 * API (i.e. the redux/RTK response carries a `pagination.total_records`). It
 * owns the `page` and `pageSize` state and returns an `onChange` that is wired
 * straight into `CustomTable`'s `pagination.onChange`, so a page click and a
 * "Rows per page" change are both handled correctly — including the reset to
 * page 1 whenever the page size changes. This removes the whole class of
 * copy-pasted pagination bugs (unbound `current`, page-only `onChange`,
 * `pageSize` never sent to the API, missing page-1 reset on size change).
 *
 * Usage:
 *   const { page, pageSize, onChange, reset } = useServerPagination();
 *
 *   // Refetch whenever page / pageSize / search change:
 *   useEffect(() => {
 *     dispatch(getList({ page, per_page: pageSize, search }));
 *   }, [page, pageSize, search]);
 *
 *   // Reset to page 1 when the search/filter changes:
 *   useEffect(reset, [search]);
 *
 *   <CustomTable
 *     columns={columns}
 *     data={rows}
 *     showPaginationMeta
 *     pagination={{
 *       total: pagination?.total_records || 0,
 *       current: page,
 *       pageSize,
 *       onChange
 *     }}
 *   />
 *
 * IMPORTANT: the page-size selector only actually resizes the list if the API
 * honors `per_page`. Confirm the endpoint reads it before relying on it.
 *
 * @param {object}  [options]
 * @param {number}  [options.defaultPage=1]       Initial page.
 * @param {number}  [options.defaultPageSize=10]  Initial rows per page.
 * @returns {{
 *   page: number,
 *   pageSize: number,
 *   setPage: (n: number) => void,
 *   setPageSize: (n: number) => void,
 *   onChange: (page: number, pageSize?: number) => void,
 *   reset: () => void
 * }}
 */
const useServerPagination = ({
  defaultPage = 1,
  defaultPageSize = 10
} = {}) => {
  const [page, setPage] = useState(defaultPage);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // Wire directly to CustomTable's `pagination.onChange`:
  //   - page-size Select fires onChange(1, newSize)  → change size, reset to 1
  //   - page buttons fire     onChange(nextPage, pageSize)
  const onChange = useCallback(
    (nextPage, nextSize) => {
      if (nextSize && nextSize !== pageSize) {
        setPageSize(nextSize);
        setPage(1);
      } else {
        setPage(nextPage);
      }
    },
    [pageSize]
  );

  // Call when a search/filter changes so the next fetch starts at page 1.
  const reset = useCallback(() => setPage(1), []);

  return { page, pageSize, setPage, setPageSize, onChange, reset };
};

export default useServerPagination;
